<template>
  <div class="card" :class="{ disabled: isExpired }">
    <FlashMessage
      v-if="downloadError"
      state="error"
      :message="$t('pvDocEditor.downloadError')"
    />
    <div
      class="card_image"
      :class="{ clickable: !isExpired }"
      @click.stop="emitClick"
    >
      <div>
        <button class="button secondary upload_link">
          {{ $t("pvCardDoc.updateDocument") }}
        </button>

        <!--<div v-if="pvdoc.updatingDoc"></div> -->
        <div class="thumbnail">
          <img :src="getStorageProxyUrl(doc.thumbnailDownloadUrl)" />
        </div>

        <div class="badge_container">
          <div class="badge" v-show="!doc.isPublished">
            {{ $t("pvCardDoc.badge.draft") }}
          </div>

          <div class="badge error" v-show="isExpired">
            {{ $t("pvCardDoc.badge.expired") }}
          </div>

          <!--
          <div class="badge" v-show="pvdoc.hasLiteratureReferences()">
            {{ $t("pvCardDoc.badge.references") }}
          </div>
          -->
        </div>
      </div>
    </div>

    <div class="card_content">
      <div class="top_title">
        <div class="card_info">
          {{ displayDate }}
        </div>
        <span class="line">&#124;</span>
        <span>{{ doc.topTitle }}</span>
      </div>

      <div class="card_title">
        {{ doc.displayTitle }}
      </div>

      <div class="teaser">
        <span v-if="hasTeaserText">{{ doc.teaserText }}</span>
      </div>

      <div class="row tags" v-if="hasTags">
        {{ $t("pvCardDoc.info.tags") }}:
        <span class="tag" v-for="(tag, idx) in tags" :key="idx">
          {{ tag }}
        </span>
      </div>

      <div class="bottom">
        <span v-if="isVideo">
          {{ $t("pvCardDoc.info.duration") }}: {{ duration }}
        </span>

        <span v-else>
          {{ $t("pvCardDoc.info.pages") }}: {{ doc.numNormalPages }}
        </span>

        <div class="buttons">
          <Tooltip v-if="canDownload">
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvDocEditor.download')"
                :disabled="downloading"
                @click.stop="download(doc.id)"
              >
                <span class="material-symbols-outlined filled">download</span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.download") }}</template>
          </Tooltip>
          <Tooltip
            v-if="
              canUseSharedSpaces && doc.doesSupportSharedspace && !isExpired
            "
          >
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvCardDoc.tooltip.share')"
                @click.stop="emitAddToSpace"
              >
                <span class="material-symbols-outlined filled"> share </span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvCardDoc.tooltip.share") }}</template>
          </Tooltip>

          <Tooltip
            v-if="
              store.getters.currentWorkspace?.root?.myPrivileges
                ?.mayAdministrate && !isExpired
            "
          >
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvCardDoc.tooltip.move')"
                @click.stop="emitMove"
              >
                <span class="material-symbols-outlined filled">
                  drive_file_move
                </span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvCardDoc.tooltip.move") }}</template>
          </Tooltip>

          <Tooltip
            v-if="
              store.getters.currentWorkspace?.root?.myPrivileges
                ?.mayAdministrate
            "
          >
            <template v-slot>
              <router-link
                :to="editorRoute"
                @click.stop
                data-test="goto-doc-editor-ctrl"
              >
                <span class="material-symbols-outlined filled"> create </span>
              </router-link>
            </template>
            <template #tooltip>{{ $t("pvCardDoc.tooltip.edit") }}</template>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Formatter, getStorageProxyUrl, MimeType } from "@/util";
import { WorkspaceDocumentFragment } from "@/graphql/operations";
import { reactive, computed, PropType } from "vue";
import { FlashMessage, Tooltip } from "@/components";
import { useDocumentDownload } from "@/composables/useDocumentDownload";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

const props = defineProps({
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: true,
  },
});

const emit = defineEmits(["copy", "move", "share", "click"]);

const emitCopy = () => emit("copy");
const emitMove = () => emit("move");
const emitAddToSpace = () => emit("share");

const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);
const route = useRoute();
const { download, downloading, downloadError } = useDocumentDownload();
const canDownload = computed(() =>
  Boolean(
    props.doc.allowUserDownload ||
      store.getters.currentWorkspace?.root?.myPrivileges?.mayAdministrate,
  ),
);

const hasTeaserText = computed(() => "teaserText" in props.doc);
const hasTags = computed(
  () =>
    "tags" in props.doc &&
    props.doc.tags !== undefined &&
    props.doc.tags.length > 0,
);
const tags = computed(() => props.doc.tags);
const pagesTotal = props.doc.pages;
const displayDate = computed(() =>
  Formatter.formatDate(new Date(props.doc.displayDate), "%Y-%m-%d"),
);
const duration = computed(() => Formatter.formatDuration(props.doc.pages));
const editorRoute = computed(() => ({
  name: "DocumentProperties",
  params: {
    workspaceId: route.params.workspaceId,
    documentId: props.doc.id,
  },
}));
const isVideo = computed(() => MimeType.isVideo(props.doc.mimeType));
const isExpired = computed(
  () => props.doc.documentExpiration && props.doc.documentExpiration.expiredAt,
);

function emitClick() {
  if (isExpired.value) {
    return;
  }

  emit("click");
}
</script>

<style lang="scss" scoped>
$img_height: 250px;
$content_height: 250px;
.card {
  border-radius: 0.3em;
  border: 1px solid var(--classic-border-strong, #cecece);
  color: var(--classic-text, #4a4a4a);
  max-width: 100%;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}

.card_image {
  display: block;
  height: $img_height;
  overflow: hidden;
  position: relative;
  top: 0;
  left: 0;
  pointer-events: none;
  border-bottom: 1px solid var(--classic-border-strong, #cecece);
}

.card_image.clickable {
  cursor: pointer;
  pointer-events: auto;
}

.card_image .upload_link {
  display: none;
}

.thumbnail {
  width: 100%;
  height: $img_height;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card_image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.badge_container {
  position: absolute;
  top: 15px;
  right: 0;
}

.badge {
  background-color: var(--classic-surface-raised, #f9f9f9);
  font-size: 0.85em;
  padding: 0 10px;
  line-height: 1.8em;
  font-weight: 500;
  margin-bottom: 5px;
}

.badge.error {
  background: $color-error;
  color: #fff;
}

.card_content {
  padding: 0.8rem 1.2rem 0.6rem 1.2rem;
  height: $content_height;
  box-sizing: border-box;
  position: relative;
}

.draggable .card.disabled {
  pointer-events: none;
  cursor: initial;
}

.card.disabled .thumbnail,
.card.disabled .top_title,
.card.disabled .card_title {
  opacity: 0.5;
}

.card_info {
  float: left;
}

.card_title {
  font-weight: 600;
  font-size: 1.1em;
  line-height: 1.125;
  color: var(--classic-text, #363636);
  margin-bottom: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.top_title {
  font-size: 0.9em;
  line-height: 1.4em;
}

.top_title span {
  display: inline-block;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 215px;
  overflow: hidden;
}

.top_title .line {
  padding: 0 0.5em;
}

.teaser {
  font-size: 0.9em;
  color: var(--classic-text-secondary, rgba(25, 23, 17, 0.6));
  height: 85px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
}

.row {
  margin-top: 8px;
}

.bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.8rem 1.2rem 0.6rem 1.2rem;
  font-size: 0.9em;
  line-height: 38px;
  height: 38px;
}

.buttons {
  float: right;
  cursor: pointer;
  pointer-events: all;
}

.buttons .material-symbols-outlined {
  font-size: 1.1em;
  border: 1px solid var(--classic-border-subtle, #ebebeb);
  border-radius: 50%;
  padding: 0.58em;
  line-height: 1;
}

.buttons a {
  color: inherit;
}

.buttons .icon_action {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
}

.buttons > div {
  display: inline-block;
  margin-left: 0.3em;
}

.buttons .material-symbols-outlined:hover {
  cursor: pointer;
  background: var(--classic-surface-inset, #f4f4f4);
}

label {
  font-size: 0.9em;
}

.row.tags {
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 0.9em;
}

.tag {
  background-color: var(--classic-tag-surface, #eaeaea);
  padding: 0.5em 0.8em;
  border-radius: 2em;
  margin-left: 0.3em;
  display: inline-block;
  font-size: 0.9em;
}
</style>
